import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement
} from 'chart.js';
import { Activity, AlertCircle } from 'lucide-react';

// --- IMPORTACIÓN DE ÓRGANOS (COMPONENTES) ---
import AdminStatCard from '../components/admin/AdminStatCard';
import AdminActivityChart from '../components/admin/AdminActivityChart';
import AdminTokenDoughnut from '../components/admin/AdminTokenDoughnut';
import AdminTable from '../components/admin/AdminTable';
import AdminContextCard from '../components/admin/AdminContextCard';
import AdminUserSelector from '../components/admin/AdminUserSelector';
import AdminAudioCard from '../components/admin/AdminAudioCard';

import './admin-dashboard.css';

// Registrar ChartJS globalmente
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement);

const AdminDashboard = () => {
  // Estado de Datos
  const [globalUsage, setGlobalUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  // Estado de UI y Filtros
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState('global');

  // Vacio: ruta relativa, como el resto de la aplicacion. El proxy de Vite la
  // resuelve en desarrollo y el reenvio de `vercel.json` en produccion, y asi
  // la llamada sale al mismo origen que la pagina — sin CORS de por medio.
  const API_URL = import.meta.env.VITE_API_URL || '';

  // 🔥 CORRECCIÓN 1: Mejor manejo de errores en carga de usuarios
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/analytics/users/list`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success) {
          setUsersList(data.data);
        } else {
          console.error('Error en respuesta de usuarios:', data);
        }
      } catch (err) {
        console.error('Error cargando usuarios:', err);
        // No bloqueamos el dashboard por error de usuarios
      }
    };
    fetchUsers();
  }, []);

  // 🔥 CORRECCIÓN 2: Fetch de datos con verificación completa
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Construir URLs dinámicas según filtro
        let usageUrl = `${API_URL}/api/analytics/usage/summary`;
        let historyUrl = `${API_URL}/api/analytics/activity/history/7`;

        if (selectedUser !== 'global') {
          const queryParam = `?user_id=${selectedUser}`;
          usageUrl += queryParam;
          historyUrl += queryParam;
        }

        console.log('🔄 Fetching data from:', usageUrl); // Debug

        const [usageRes, historyRes, leaderRes] = await Promise.all([
          fetch(usageUrl),
          fetch(historyUrl),
          fetch(`${API_URL}/api/analytics/activity/leaderboard?limit=5`)
        ]);

        // 🔥 VERIFICACIÓN CRÍTICA: Checar status HTTP
        if (!usageRes.ok) throw new Error(`API Error: ${usageRes.status}`);
        if (!historyRes.ok) console.warn('History API warning:', historyRes.status);
        if (!leaderRes.ok) console.warn('Leader API warning:', leaderRes.status);

        const usageData = await usageRes.json();
        const historyData = await historyRes.json();
        const leaderData = await leaderRes.json();

        console.log('📊 Datos recibidos del backend:', usageData); // Debug

        // 🔥 VERIFICACIÓN CRÍTICA: Checar success flag
        if (usageData.success) {
          setGlobalUsage(usageData.data);
        } else {
          throw new Error(usageData.detail || 'Error en datos de uso');
        }

        if (historyData.success) setHistory(historyData.data);
        if (leaderData.success) setLeaderboard(leaderData.data);

      } catch (err) {
        console.error("❌ Error fetching admin data:", err);
        setError(`Error de conexión: ${err.message}`);

        // 🔥 DATOS DE FALLBACK PARA DEBUG
        setGlobalUsage({
          global_token_usage: 0,
          token_split: { input: 0, output: 0 },
          financials: {
            total_cost_historical: 0.0,
            cost_this_month: 0.0,
            projected_monthly_bill: 0.0
          },
          total_interactions: 0,
          top_spenders: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedUser, API_URL]);

  // 🔥 CORRECCIÓN 3: Función segura para formatear números
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `$${num.toFixed(4)}`; // Mostrar 4 decimales para ver centavos
  };

  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString();
  };

  if (loading && !globalUsage) {
    return (
      <div className="ad-loading">
        <Activity className="ad-spin" />
        Cargando datos del dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="ad-error">
        <AlertCircle />
        {error}
        <br />
        <small>Verifica que el backend esté corriendo en: {API_URL}</small>
      </div>
    );
  }

  return (
    <div className="ad-container">
      {/* HEADER CON SELECTOR */}
      <header className="ad-header">
        <div>
          <h1>🕵️‍♂️ Centro de Mando</h1>
          <p>Estado Operativo: <strong>En Línea</strong></p>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <AdminUserSelector
            users={usersList}
            selectedUser={selectedUser}
            onChange={setSelectedUser}
          />
          <div className="ad-status-badge">v3.1 TokenFix</div>
        </div>
      </header>

      {/* TARJETA DE CONTEXTO */}
      <div style={{ marginBottom: '2rem' }}>
        <AdminContextCard />
      </div>

      {/* KPI GRID (Tarjetas Superiores) - CORREGIDAS */}
      <div className="ad-grid-kpi">
        <AdminStatCard
          title={selectedUser === 'global' ? "Costo Histórico" : "Gasto Usuario"}
          value={formatCurrency(globalUsage?.financials?.total_cost_historical)}
          icon="dollar"
          color="#10B981"
        />
        <AdminStatCard
          title="Mes Actual"
          value={formatCurrency(globalUsage?.financials?.cost_this_month)}
          icon="wallet"
          color="#3b82f6"
        />
        <AdminStatCard
          title="Proyección Mes"
          value={formatCurrency(globalUsage?.financials?.projected_monthly_bill)}
          icon="trend"
          color="#f59e0b"
        />
        <AdminStatCard
          title="Total Tokens (Chat)"
          value={formatNumber(globalUsage?.global_token_usage)}
          icon="zap"
          color="#6366f1"
        />
      </div>

      <div className="ad-grid-main">
        {/* === COLUMNA IZQUIERDA === */}
        <div className="ad-column-left">
          {/* 1. Gráfica de Línea */}
          <AdminActivityChart history={history} />

          {/* 2. Sub-Grid (Dona y Top Gasto) */}
          <div className="ad-grid-sub">
            {/* Gráfica de Dona - CORREGIDA */}
            <AdminTokenDoughnut
              input={globalUsage?.token_split?.input || 0}
              output={globalUsage?.token_split?.output || 0}
              total={globalUsage?.global_token_usage || 0}
            />

            {/* Tabla de Top Spenders - CORREGIDA */}
            <AdminTable
              title={selectedUser === 'global' ? "🐋 Top Gasto" : "👤 Detalle"}
              columns={['Usuario', 'Gasto ($)']}
              data={globalUsage?.top_spenders || []}
              renderRow={(user, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="ad-user-cell">
                      <div className="ad-avatar-circle small">
                        {user.alias?.charAt(0) || "U"}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.9rem' }}>
                          {user.alias || 'Usuario'}
                        </span>
                        <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                          {user.tokens ? `${user.tokens} tokens` : 'Sin datos'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="ad-num">
                    {formatCurrency(user.cost)}
                  </td>
                </tr>
              )}
            />
          </div>
        </div>

        {/* === COLUMNA DERECHA === */}
        <div className="ad-column-right">

          {/* 1. Tabla de Líderes - CORREGIDA */}
          <AdminTable
            title="🏆 Líderes Hoy"
            columns={['Estudiante', 'Score']}
            data={leaderboard}
            renderRow={(student, idx) => (
              <tr key={idx}>
                <td>
                  <div className="ad-user-cell">
                    <div className="ad-avatar-circle">
                      {(student.name || 'A').charAt(0)}
                    </div>
                    <span>{student.name || 'Anónimo'}</span>
                  </div>
                </td>
                <td className="ad-num">
                  {(student.words_spoken || 0) + (student.words_written || 0)}
                </td>
              </tr>
            )}
          />

          {/* 2. Tarjeta de Audio (TTS) */}
          <div style={{ marginTop: '2rem' }}>
            <AdminAudioCard stats={globalUsage?.audio_stats} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;